import { NextPage } from 'next';
import { DefaultSeo } from 'next-seo';
import { AppProps } from 'next/app';
import Router from 'next/router';
import nProgress from 'nprogress';
import { QueryClient, QueryClientProvider } from 'react-query';
import { RecoilEnv, RecoilRoot } from 'recoil';
import { MyCustomTheme } from 'themes';

import { getBasicTokenString } from '@helpers/credentials';
import { AuthProvider } from 'providers/useAuth';

import { ChakraProvider } from '@chakra-ui/provider';
import { createStandaloneToast } from '@chakra-ui/toast';
import { Nunito, Nunito_Sans } from 'next/font/google';
import seoConfig from '../next-seo.config';

const { ToastContainer } = createStandaloneToast();

Router.events.on('routeChangeStart', nProgress.start);
Router.events.on('routeChangeError', nProgress.done);
Router.events.on('routeChangeComplete', nProgress.done);

import { AlertProvider } from '@providers/stockAlert';
import '@styles/print.style.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      suspense: false,
    },
    mutations: {
      retry: false,
    },
  },
});
RecoilEnv.RECOIL_DUPLICATE_ATOM_KEY_CHECKING_ENABLED = false;

const fontHeading = Nunito_Sans({
  weight: ['400', '500', '600', '700', '800'],
  subsets: ['latin'],
});
const fontBody = Nunito({
  weight: ['400', '500', '600', '700', '800'],
  subsets: ['latin'],
});

interface CustomAppProps extends AppProps {
  Component: NextPage;
}

const App: NextPage<CustomAppProps> = ({ Component, pageProps }) => {
  const token = getBasicTokenString();
  return (
    <>
      <style jsx global>
        {`
          :root {
            --font-heading: ${fontHeading.style.fontFamily};
            --font-body: ${fontBody.style.fontFamily};
          }
        `}
      </style>
      <DefaultSeo {...seoConfig} />
      <RecoilRoot>
        <ChakraProvider theme={MyCustomTheme}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider token={token} initialProfile={pageProps?.profile}>
              <AlertProvider>
                <Component {...pageProps} />
                <ToastContainer />
              </AlertProvider>
            </AuthProvider>
          </QueryClientProvider>
        </ChakraProvider>
      </RecoilRoot>
    </>
  );
};

export default App;
